import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Globe, ShieldCheck, ArrowRight, Crown, Lock, Loader2, Copy, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * EnterpriseBrandingCard
 * ──────────────────────
 * Surfaces the white-label / custom-domain console for Enterprise-tier
 * tenants. Non-Enterprise tiers see a teaser CTA that routes to the
 * Enterprise consultation form.
 */
export default function EnterpriseBrandingCard() {
  const [state, setState] = useState(null);   // { tier, branding, white_label_enabled }
  const [form, setForm] = useState({ logo_url: "", primary_color: "", custom_domain: "", display_name: "" });
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/teams/me/branding");
      setState(data);
      const b = data.branding || {};
      setForm({
        logo_url:       b.logo_url || "",
        primary_color:  b.primary_color || "",
        custom_domain:  b.custom_domain || "",
        display_name:   b.display_name || "",
      });
    } catch {
      // Tier endpoint missing or unauthorized — silently hide the card.
    }
  };
  useEffect(() => { load(); }, []);

  if (!state) return null;

  const enabled = state.white_label_enabled;

  // Non-Enterprise teaser
  if (!enabled) {
    return (
      <section className="rounded-xl border p-6" style={{ backgroundColor: "rgba(255,215,0,0.05)", borderColor: "rgba(255,215,0,0.35)" }} data-testid="branding-teaser-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.2em] font-bold uppercase mb-2"
              style={{ backgroundColor: "rgba(255,215,0,0.18)", color: "#FFD700" }}>
              <Crown className="h-3 w-3" /> Enterprise tier
            </div>
            <h2 className="font-display font-bold text-xl text-white">White-label, custom domains, executive dashboards</h2>
            <p className="text-sm text-white/65 mt-2 leading-relaxed">
              Unlock tenant-branded consoles on your own domain — full custom logo, colors, and email sender. Available exclusively on the Enterprise tier.
            </p>
          </div>
          <Link to="/enterprise/contact">
            <Button className="text-[var(--ink-0)] font-bold rounded-full" style={{ backgroundColor: "#FFD700" }} data-testid="branding-upgrade-cta">
              Book Enterprise consultation <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    );
  }

  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => !!v));
      const { data } = await api.patch("/teams/me/branding", payload);
      setState((s) => ({ ...(s || {}), branding: data.branding }));
      toast.success("Branding saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      await api.post("/teams/me/branding/verify");
      toast.success("Domain verified");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setVerifying(false); }
  };

  const branding = state.branding || {};
  const verified = !!branding.dns_verified;
  const dnsToken = branding.dns_token;
  const dnsQname = branding.custom_domain ? `_armiq.${branding.custom_domain}` : "—";

  const copyToken = async () => {
    if (!dnsToken) return;
    await navigator.clipboard.writeText(dnsToken).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <section className="rounded-xl ink-card p-6 space-y-6" data-testid="branding-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/40">Enterprise · white-label</div>
          <h2 className="font-display font-bold text-xl text-white mt-1 flex items-center gap-2">
            <Crown className="h-4 w-4" style={{ color: "#FFD700" }} /> Branded console
          </h2>
          <p className="text-sm text-white/60 mt-1">Your tenant&apos;s logo, color, and custom domain.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-white/70">Display name</Label>
          <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="PitchLab Performance" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="branding-display-name" />
        </div>
        <div>
          <Label className="text-white/70">Primary color (hex)</Label>
          <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} placeholder="#D90429" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white font-mono-num" data-testid="branding-primary-color" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-white/70">Logo URL</Label>
          <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://cdn.your-org.com/logo.svg" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="branding-logo-url" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-white/70">Custom domain</Label>
          <Input value={form.custom_domain} onChange={(e) => setForm({ ...form, custom_domain: e.target.value })} placeholder="pitchlab.example.com" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="branding-custom-domain" />
        </div>
      </div>

      <div className="flex items-center gap-3 justify-end">
        <Button onClick={save} disabled={busy} className="text-white red-glow font-bold" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="branding-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save branding"}
        </Button>
      </div>

      {/* DNS verification — only shown after a custom_domain is saved */}
      {branding.custom_domain && (
        <div className="rounded-lg border p-4" style={{ backgroundColor: verified ? "rgba(16,185,129,0.06)" : "rgba(245,158,11,0.06)", borderColor: verified ? "rgba(16,185,129,0.4)" : "rgba(245,158,11,0.4)" }} data-testid="branding-dns-status">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold"
                style={{ color: verified ? "#10B981" : "#F59E0B" }}>
                {verified ? <ShieldCheck className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                {verified ? "Domain verified" : "Verification required"}
              </div>
              <div className="text-sm text-white mt-1.5">
                {verified
                  ? <>Verified <span className="text-white/60">on {new Date(branding.dns_verified_at).toLocaleDateString()}</span></>
                  : "Publish the TXT record below, then click Verify."}
              </div>
              {!verified && (
                <div className="mt-3 text-xs text-white/65 space-y-1">
                  <div>1. Add a TXT record on <code className="bg-[var(--ink-1)] px-1.5 py-0.5 rounded text-white">{dnsQname}</code></div>
                  <div className="flex items-center gap-2">
                    2. Value:
                    <code className="bg-[var(--ink-1)] px-1.5 py-0.5 rounded text-white font-mono-num">{dnsToken}</code>
                    <button onClick={copyToken} className="text-white/60 hover:text-white" data-testid="branding-dns-copy">
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                  <div>3. Click Verify (TXT propagation usually takes &lt;15 minutes).</div>
                </div>
              )}
            </div>
            <Button onClick={verify} disabled={verifying || verified} variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="branding-verify-btn">
              {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : verified ? <><Lock className="h-4 w-4 mr-2" /> Verified</> : <><Globe className="h-4 w-4 mr-2" /> Verify domain</>}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
